<template>
  <div class="checkpoint-edit-form-container">
    <CheckpointHeader 
      :state="checkpointState"
      :readiness_level="readiness_level"
      :criteria="criteria"
      :checkpointPrefix="checkpointPrefix"
      :instructions="instructions"
      :breadcrumbs="breadcrumbs"
      :checkpointId="checkpointId"
      :responsibilityId="responsibilityId"
      :checkpointLevel="checkpointLevel"
      :checkpointStatus="checkpointStatus"
      :checkpointInstructions="instructionsContentString"
      @breadcrumb-click="handleBreadcrumbClick"
      @checkpoint-updated="handleCheckpointUpdated"
      @open-chatter="openChatter"
    />
    <!-- <GuideVideoBanner 
      :text="videoBannerText"
      :subtext="videoBannerSubText"
      :videoUrl="videoUrl"
      :videoThumbnail="videoThumbnail" 
    /> -->
    <CheckpointEditMenu
      :instructions="instructionsState"
      :instructionsContent="instructionsContentData"
      :instructionsIsPublished="instructionsIsPublishedData"
      :instructionsChange="instructionsChangeData"
      :microlearning="microlearningState"
      :microlearningVideoUrl="microlearningVideoUrl"
      :microlearningVideoId="microlearningVideoId"
      :checkpointId="checkpointId"
      :responsibilityId="responsibilityId"
      :checkpointCriteria="checkpointCriteria"
      :checkpointPrefix="checkpointPrefix"
      :checkpointLevel="checkpointLevel"
      :checkpointStatus="checkpointStatus"
      :quizz="quizState"
      :show_quiz="false"
      :responsibilityCode="responsibilityCode"
      :responsibilityName="responsibilityName"
      :responsibilityPurpose="responsibilityPurpose"
      :checkpointInstructions="instructionsContentString"
      :set_to_microlearning_instructions="set_to_microlearning_instructions"
      :microlearningInstructions="microlearningInstructions"
      :instructions_draft="instructions_draft"
      @instructions-publish="handleInstructionsPublish"
      @instructions-discard="handleInstructionsDiscard"
      @instructions-input="handleInstructionsInput"
      @instructions-updated="handleInstructionsUpdated"
      @video-uploaded="handleVideoUploaded"
      @video-deleted="handleVideoDeleted"
      @refresh-checkpoint="handleRefreshCheckpoint"
      :isStorybook="isStorybook"
    />
    <ChecklistDrawer
      ref="drawerContainer"
      :drawer="drawer"
      :listLevel="checklistLevel"
      :listCriteria="checklistCriteria"
      :listData="listData"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalCheckpointChatter"
      @closeDrawer="closeDrawer"
      @requestMoreCheckpointsChatter="handleRequestMoreChatter"
    />
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import CheckpointHeader from '@components/CheckpointHeader/CheckpointHeader.vue';
import GuideVideoBanner from '@components/GuideVideoBanner/GuideVideoBanner.vue';
import CheckpointEditMenu from '@components/CheckpointEditMenu/CheckpointEditMenu.vue';
import ChecklistDrawer from '@components/ChecklistCriteriaTable/ChecklistDrawer.vue';

const emit = defineEmits([
  'back-to-list',
  'instructions-publish',
  'instructions-discard',
  'instructions-input',
  'instructions-updated',
  'video-uploaded',
  'video-deleted',
  'refresh-checkpoint',
  'checkpoint-updated',
  'get-chatter-list',
  'requestMoreCheckpointsChatter'
]);

// Breadcrumbs for demonstration
const breadcrumbs = ref([
  { text: "Checklist", href: "" },
  { text: "Checkpoint", href: "", disabled: true }
]);

const handleBreadcrumbClick = (breadcrumb) => {
  if (breadcrumb.text === "Checklist") {
    emit('back-to-list');
  }
};

const props = defineProps({
  checkpointState: {
    type: String,
    default: 'Editable'
  },
  readiness_level: {
    type: Object,
    default: () => ({ id: 1, name: 'Apprentice', value: 'apprentice' })
  },
  criteria: {
    type: String,
    default: ''
  },
  instructions: {
    type: String,
    default: ''
  },
  // Video banner props
  videoBannerText: {
    type: String,
    default: 'You are a Master if you have at least one current Master badge for a Responsibility'
  },
  videoBannerSubText: {
    type: String,
    default: 'Learn about what is a master and what is expected of you as a master. We rely on masters to create inspection checklists to be used by coaches to certify readiness of employees as well as train and certify coaches to increase readiness building capacity.'
  },
  videoUrl: {
    type: String,
    default: 'https://example.com/video.mp4'
  },
  videoThumbnail: {
    type: String,
    default: 'https://placehold.co/160x90'
  },
  // CheckpointEditMenu props
  instructionsState: {
    type: String,
    default: 'add'
  },
  // Instructions content for the editor
  instructionsContent: {
    type: Object,
    default: null
  },
  instructionsIsPublished: {
    type: Boolean,
    default: false
  },
  instructionsChange: {
    type: Object,
    default: null
  },
  microlearningState: {
    type: String,
    default: 'add'
  },
  // Microlearning video URL (when video exists)
  microlearningVideoUrl: {
    type: String,
    default: null
  },
  // Microlearning video ID (for delete/manage operations)
  microlearningVideoId: {
    type: [String, Number],
    default: null
  },
  // Checkpoint ID for upload
  checkpointId: {
    type: [String, Number],
    default: null
  },
  // Responsibility ID for API calls
  responsibilityId: {
    type: [String, Number],
    default: null
  },
  // Checkpoint data needed for API payload
  checkpointCriteria: {
    type: String,
    default: ''
  },
  checkpointPrefix: {
    type: String,
    default: ''
  },
  checkpointLevel: {
    type: [Number, String],
    default: 1
  },
  checkpointStatus: {
    type: String,
    default: 'active'
  },
  quizState: {
    type: String,
    default: 'add'
  },
  showQuiz: {
    type: Boolean,
    default: true
  },
  // Chatter props
  chatterList: {
    type: Array,
    default: () => []
  },
  totalCheckpointChatter: {
    type: Number,
    default: 0
  },
  // Responsibility data for IQ prompt
  responsibilityCode: {
    type: String,
    default: ''
  },
  responsibilityName: {
    type: String,
    default: ''
  },
  responsibilityPurpose: {
    type: String,
    default: ''
  },
  // Checkpoint instructions for IQ prompt (business_purpose)
  checkpointInstructions: {
    type: String,
    default: ''
  },
  microlearningInstructions: {
    type: String,
    default: ''
  },
  isStorybook: {
    type: Boolean,
    default: false
  },
  set_to_microlearning_instructions: {
    type: Boolean,
    default: false
  },
  instructions_draft: {
    type: String,
    default: ''
  }
});

// Chatter drawer state
const drawer = ref(false);
const checklistLevel = ref('');
const checklistCriteria = ref('');
const listData = ref({});

// Computed to derive instructions content from props
const instructionsContentData = computed(() => {
  if (props.instructionsContent) {
    return props.instructionsContent;
  }
  // If we have instructions string, wrap it in content object
  if (props.instructions) {
    return { content: props.instructions };
  }
  return null;
});

// Computed to derive instructionsIsPublished - if instructionsState is 'selected', it means published
const instructionsIsPublishedData = computed(() => {
  // If explicitly set, use it
  if (props.instructionsIsPublished) {
    return props.instructionsIsPublished;
  }
  // Otherwise, derive from state - 'selected' means we have published instructions
  return props.instructionsState === 'selected';
});

// Computed to get instructions content as a string (for API payload)
const instructionsContentString = computed(() => {
  if (props.instructionsContent && props.instructionsContent.content) {
    return props.instructionsContent.content;
  }
  if (props.instructions) {
    return props.instructions;
  }
  return '';
});

// Computed to derive instructionsChange from instructions_draft
const instructionsChangeData = computed(() => {
  // If explicitly set via prop, use it
  if (props.instructionsChange) {
    return props.instructionsChange;
  }
  // Otherwise, derive from instructions_draft if it has content
  if (props.instructions_draft) {
    // Check if instructions_draft has meaningful content
    const strippedContent = props.instructions_draft
      .replace(/<[^>]*>/g, '')      // Remove HTML tags
      .replace(/&nbsp;/g, '')        // Remove &nbsp;
      .replace(/\s+/g, '')           // Remove whitespace
      .trim();
    if (strippedContent.length > 0) {
      return { content: props.instructions_draft };
    }
  }
  return null;
});

// Event handlers
const handleInstructionsPublish = (content) => {
  emit('instructions-publish', content);
};

const handleInstructionsDiscard = () => {
  emit('instructions-discard');
};

const handleInstructionsInput = (content) => {
  emit('instructions-input', content);
};

const handleInstructionsUpdated = (data) => {
  emit('instructions-updated', data);
};

const handleVideoUploaded = (payload) => {
  // Pass the full payload (includes checkpointData) to parent
  emit('video-uploaded', payload);
};

const handleVideoDeleted = () => {
  // Bubble up to parent to refresh checkpoint data after video deletion
  emit('video-deleted');
};

const handleRefreshCheckpoint = () => {
  // Bubble up to parent to refresh checkpoint data (for video processing)
  emit('refresh-checkpoint');
};

const handleCheckpointUpdated = (data) => {
  // Bubble up to parent to update checkpoint data in real-time
  emit('checkpoint-updated', data);
};

// Chatter handlers
const openChatter = (data) => {
  checklistLevel.value = data.level;
  checklistCriteria.value = data.criteria;
  listData.value = data;
  drawer.value = true;
  emit('get-chatter-list', data);
};

const closeDrawer = (data) => {
  drawer.value = false;
};

const handleRequestMoreChatter = (event) => {
  emit('requestMoreCheckpointsChatter', event);
};
</script>

<style scoped>
.checkpoint-edit-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.checkpoint-edit-form-container .guide-video-banner {
  margin-top: 0px;
}
</style>
